import { TriangleAlert } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import type { Inquiry, InquiryCargo } from '@/lib/api/types'

/**
 * The shipment as the customer described it when asking for a quote: what
 * staff need to price it, without opening the email it came with.
 */

const SERVICES: Record<string, string> = {
  DESTINATION_CLEARANCE: 'Customs clearance at destination',
  DOOR_DELIVERY: 'Door delivery',
  PICKUP: 'Pickup from supplier',
  EXPORT_CLEARANCE: 'Export clearance in China',
  INSPECTION: 'Pre-shipment inspection',
  PVOC: 'PVoC / certificate of conformity',
  INSURANCE: 'Cargo insurance',
  TRACKING: 'Shipment tracking',
}

const MODES = { AIR: 'Air freight', SEA: 'Sea freight', UNSURE: 'Not chosen' } as const
const LEVELS = {
  EXPRESS: 'Express',
  STANDARD: 'Regular',
  COURIER: 'Courier',
  CHARTER: 'Charter (whole aircraft)',
} as const
const CLIENTS = {
  GOVERNMENT: 'Government or public body',
  HUMANITARIAN: 'Humanitarian or development organisation',
  BUSINESS: 'Company or project',
  PRIVATE: 'Private client',
} as const

function amount(value: number | null | undefined, unit: string) {
  return value || value === 0 ? `${value.toLocaleString()} ${unit}` : null
}

function formatDay(value: string | null | undefined) {
  if (!value) return null
  const date = new Date(`${value}T00:00:00`)
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

function load(cargo: InquiryCargo) {
  if (cargo.load_type === 'FCL') {
    const containers = (cargo.containers ?? []).map((entry) => `${entry.count} × ${entry.type}`)
    return containers.length ? `FCL · ${containers.join(', ')}` : 'FCL'
  }
  return cargo.load_type === 'LCL' ? 'LCL (shared container)' : null
}

export function InquiryCargoCard({ inquiry }: { inquiry: Pick<Inquiry, 'cargo' | 'source'> }) {
  const cargo = inquiry.cargo
  if (!cargo) return null

  const facts = [
    ['Mode', cargo.mode ? MODES[cargo.mode] : null],
    ['Load', load(cargo)],
    ['Weight', amount(cargo.weight_kg, 'kg')],
    ['Volume', amount(cargo.volume_cbm, 'CBM')],
    ['Pieces', cargo.pieces ? String(cargo.pieces) : null],
    ['Size per piece', cargo.dimensions],
    ['Service', cargo.service_level ? LEVELS[cargo.service_level] : null],
    ['For', cargo.client_type ? CLIENTS[cargo.client_type] : null],
    ['Ready', formatDay(cargo.ready_date)],
    ['Wanted by', formatDay(cargo.arrival_date)],
  ].filter((entry): entry is [string, string] => Boolean(entry[1]))
  const services = cargo.services ?? []

  return (
    <Card data-testid="inquiry-cargo">
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle>Shipment requested</CardTitle>
          {inquiry.source === 'WEBSITE' && <Badge variant="secondary">Website</Badge>}
        </div>
        <CardDescription>As the customer described it when asking for a quote.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {facts.length > 0 && (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
            {facts.map(([label, value]) => (
              <div key={label} className="min-w-0">
                <dt className="text-xs uppercase text-muted-foreground">{label}</dt>
                <dd className="mt-1 break-words">{value}</dd>
              </div>
            ))}
          </dl>
        )}
        {cargo.dangerous_goods && (
          <p className="flex items-center gap-2 font-medium text-destructive">
            <TriangleAlert className="h-4 w-4" aria-hidden />
            Dangerous goods{cargo.un_number ? ` · UN ${cargo.un_number}` : ''}
          </p>
        )}
        {services.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-xs uppercase text-muted-foreground">Also wants</p>
            <div className="flex flex-wrap gap-1.5">
              {services.map((service) => (
                <Badge key={service} variant="outline">
                  {SERVICES[service] ?? service}
                </Badge>
              ))}
            </div>
          </div>
        )}
        {cargo.pickup_address && (
          <div>
            <p className="text-xs uppercase text-muted-foreground">Pickup address</p>
            <p className="mt-1 whitespace-pre-line">{cargo.pickup_address}</p>
          </div>
        )}
        {/* The email wording stays only when the website could not upload the file. */}
        {cargo.invoice_filed ? (
          <p className="text-muted-foreground">
            The visitor&apos;s commercial invoice is filed on this inquiry, under Files.
          </p>
        ) : (
          cargo.has_invoice && (
            <p className="text-muted-foreground">
              A commercial invoice came with the website&apos;s email to the team.
            </p>
          )
        )}
      </CardContent>
    </Card>
  )
}
